import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  serverTimestamp,
  getDocs,
  DocumentData,
  QueryDocumentSnapshot,
  Timestamp,
} from 'firebase/firestore'
import { db } from 'config/firebase'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { PickupLocation, RepairShopGrade, ShopType } from '@otw/models'

/** Hook-local shape: repair shop fields plus PickupLocation audit fields used by location management. */
export interface RepairShopLocation extends PickupLocation {
  shop_type?: ShopType[]
  name?: string
  grade?: RepairShopGrade
  inspection_checklist_id?: string
  intake_checklist_id?: string
  communication?: number
  price?: number
  speed?: number
  diesel?: boolean
  secondary_phone?: string
  labor_rate?: number
  capacity?: number
  lat?: number | null
  lng?: number | null
  geocoded_address?: string | null
  is_active?: boolean
  inactive_at?: Date | null
  inactive_note?: string | null
}

interface UseRepairShopLocationsReturn {
  repairShopLocations: RepairShopLocation[] | undefined
  isLoading: boolean
  error: Error | null
  createRepairShopLocation: (location: Omit<RepairShopLocation, 'id' | 'created_at' | 'updated_at'>) => Promise<RepairShopLocation>
  updateRepairShopLocation: (location: RepairShopLocation) => Promise<RepairShopLocation>
  deleteRepairShopLocation: (id: string) => Promise<string>
}

const stripUndefinedFields = <T extends Record<string, unknown>>(obj: T): Partial<T> => {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== undefined)
  ) as Partial<T>
}

/** Missing/undefined `is_active` means active (legacy docs). */
export const isRepairShopActive = (shop: Pick<RepairShopLocation, 'is_active'>): boolean =>
  shop.is_active !== false

export function filterRepairShopsForPicker(
  shops: RepairShopLocation[] | undefined | null,
  { includeInactive = false }: { includeInactive?: boolean } = {},
): RepairShopLocation[] {
  if (!shops?.length) return []
  if (includeInactive) return shops
  return shops.filter(isRepairShopActive)
}

/** Default cap so Autocomplete does not mount thousands of list items. */
export const REPAIR_SHOP_OPTION_LIMIT = 50

const repairShopSearchHaystack = (shop: RepairShopLocation): string =>
  [
    shop.name,
    shop.contact_name,
    shop.company_name,
    shop.address,
    shop.city,
    shop.state,
    shop.zip,
    shop.contact_phone,
  ]
    .map(part => part?.trim())
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

/**
 * Filter repair shops by name, contact, or address fields.
 * Caps results so large `repair_shop_locations` lists do not freeze Autocomplete.
 */
export const filterRepairShopOptions = (
  options: readonly RepairShopLocation[],
  inputValue: string,
  opts?: { limit?: number },
): RepairShopLocation[] => {
  const limit = opts?.limit ?? REPAIR_SHOP_OPTION_LIMIT
  const q = inputValue.trim().toLowerCase()
  if (!q) {
    return options.slice(0, limit)
  }
  return options
    .filter(shop => repairShopSearchHaystack(shop).includes(q))
    .slice(0, limit)
}

const parseInactiveAt = (value: unknown): Date | null => {
  if (value == null) return null
  if (value instanceof Date) return value
  if (value instanceof Timestamp) return value.toDate()
  if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as Timestamp).toDate === 'function') {
    return (value as Timestamp).toDate()
  }
  if (typeof value === 'string') {
    const d = new Date(value)
    return Number.isNaN(d.getTime()) ? null : d
  }
  return null
}

export const useRepairShopLocations = (): UseRepairShopLocationsReturn => {
  const { showSuccess, showError } = useSnackbar()
  const queryClient = useQueryClient()
  const queryKey = ['repairShopLocations']

  const {
    data: repairShopLocations,
    isLoading,
    error,
  } = useQuery<RepairShopLocation[]>({
    queryKey,
    queryFn: async () => {
      try {
        const collectionRef = collection(db, 'repair_shop_locations')
        
        const snapshot = await getDocs(collectionRef)
        
        const locations = snapshot.docs.map((docSnap: QueryDocumentSnapshot<DocumentData>) => {
          const data = docSnap.data()
          // Handle backward compatibility: convert string shop_type to array if needed
          let shopTypeArray: ShopType[] = []
          if (data.shop_type) {
            if (Array.isArray(data.shop_type)) {
              shopTypeArray = data.shop_type
            } else if (typeof data.shop_type === 'string') {
              // Convert old string format to array, excluding 'both'
              if (data.shop_type !== 'both') {
                shopTypeArray = [data.shop_type as ShopType]
              }
            }
          }
          return {
            id: docSnap.id,
            ...data,
            shop_type: shopTypeArray,
            is_active: data.is_active === false ? false : true,
            inactive_at: parseInactiveAt(data.inactive_at),
            inactive_note:
              typeof data.inactive_note === 'string' && data.inactive_note.length > 0
                ? data.inactive_note
                : null,
          } as RepairShopLocation
        })
        
        return locations
      } catch (error) {
        throw error
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  const createRepairShopLocationMutation = useMutation({
    mutationFn: async (location: Omit<RepairShopLocation, 'id' | 'created_at' | 'updated_at'>) => {
      try {
        const sanitizedLocation = stripUndefinedFields({
          ...location,
          is_active: location.is_active !== false,
          inactive_at: location.is_active === false ? location.inactive_at ?? new Date() : null,
          inactive_note: location.inactive_note ?? null,
        } as Record<string, unknown>)
        const docRef = await addDoc(collection(db, 'repair_shop_locations'), {
          ...sanitizedLocation,
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
        })
        const result = { id: docRef.id, ...sanitizedLocation } as RepairShopLocation
        return result
      } catch (error) {
        throw error
      }
    },
    onMutate: async newLocation => {
      await queryClient.cancelQueries({ queryKey })
      const previousLocations = queryClient.getQueryData<RepairShopLocation[]>(queryKey)

      const tempId = `temp-${Date.now()}`
      const optimisticLocation = {
        ...newLocation,
        is_active: newLocation.is_active !== false,
        id: tempId,
        created_at: new Date(),
        updated_at: new Date(),
      } as RepairShopLocation

      queryClient.setQueryData<RepairShopLocation[]>(queryKey, old => [...(old || []), optimisticLocation])

      return { previousLocations, optimisticLocation, tempId }
    },
    onError: (err, variables, context) => {
      queryClient.setQueryData(queryKey, context?.previousLocations)
      showError(`Failed to create repair shop location: ${err instanceof Error ? err.message : 'Unknown error'}`)
    },
    onSuccess: (result, variables, context) => {
      // Update the cache to replace the temporary ID with the real one
      if (context?.tempId) {
        queryClient.setQueryData<RepairShopLocation[]>(queryKey, old => {
          if (!old) return [result]
          return old.map(location => (location.id === context.tempId ? result : location))
        })
      }
      showSuccess('Repair shop location created successfully')
    },
  })

  const updateRepairShopLocationMutation = useMutation({
    mutationFn: async (location: RepairShopLocation) => { 
      if (!location.id) throw new Error('Location ID is required')
      try {
        const { id, ...locationWithoutId } = location
        const sanitizedLocation = stripUndefinedFields(locationWithoutId as Record<string, unknown>)
        await updateDoc(doc(db, 'repair_shop_locations', location.id), {
          ...sanitizedLocation,
          updated_at: serverTimestamp(),
        })
        return { ...location, ...sanitizedLocation }
      } catch (error) {
        throw error
      }
    },
    onMutate: async updatedLocation => {
      await queryClient.cancelQueries({ queryKey })
      const previousLocations = queryClient.getQueryData<RepairShopLocation[]>(queryKey)

      queryClient.setQueryData<RepairShopLocation[]>(queryKey, old => {
        if (!old) return old
        return old.map(location => 
          location.id === updatedLocation.id 
            ? { ...location, ...updatedLocation, updated_at: new Date() }
            : location
        )
      })

      return { previousLocations }
    },
    onError: (err, variables, context) => {
      queryClient.setQueryData(queryKey, context?.previousLocations)
      showError(`Failed to update repair shop location: ${err instanceof Error ? err.message : 'Unknown error'}`)
    },
    onSuccess: () => {
      showSuccess('Repair shop location updated successfully')
    },
  })

  const deleteRepairShopLocationMutation = useMutation({
    mutationFn: async (id: string) => {
      try {
        await deleteDoc(doc(db, 'repair_shop_locations', id))
        return id
      } catch (error) {
        console.error('Error deleting repair shop location:', error)
        throw error
      }
    },
    onMutate: async id => {
      await queryClient.cancelQueries({ queryKey })
      const previousLocations = queryClient.getQueryData<RepairShopLocation[]>(queryKey)

      queryClient.setQueryData<RepairShopLocation[]>(queryKey, old => {
        if (!old) return old
        return old.filter(location => location.id !== id)
      })

      return { previousLocations }
    },
    onError: (err, variables, context) => {
      console.error('Delete repair shop location mutation error:', err)
      queryClient.setQueryData(queryKey, context?.previousLocations)
      showError(`Failed to delete repair shop location: ${err instanceof Error ? err.message : 'Unknown error'}`)
    },
    onSuccess: () => {
      showSuccess('Repair shop location deleted successfully')
    },
  })

  return {
    repairShopLocations,
    isLoading,
    error: error as Error | null,
    createRepairShopLocation: createRepairShopLocationMutation.mutateAsync,
    updateRepairShopLocation: updateRepairShopLocationMutation.mutateAsync,
    deleteRepairShopLocation: deleteRepairShopLocationMutation.mutateAsync,
  }
}
